import {
  OnboardingAction,
  OnboardingConfig,
  OnboardingView,
} from '@chiroup/core/types/Onboarding.type';
import {
  AlertCircle,
  CalendarDays,
  ExternalLink,
  RefreshCw,
  Rocket,
  Settings2,
} from 'lucide-react';
import { FormEvent, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import useAdminMe from '../../hooks/useAdminMe';
import useOnboarding from '../../hooks/useOnboarding';
import OnboardingChecklist from './OnboardingChecklist';
import OnboardingFiles from './OnboardingFiles';
import OnboardingTrainingMatrix from './OnboardingTrainingMatrix';
import {
  buttonClass,
  calendarLabel,
  errorMessage,
  Field,
  inputClass,
  OnboardingDraftProvider,
  secondaryClass,
  Section,
} from './OnboardingUi';

const OnboardingConfiguration = ({
  view,
  act,
  canReview,
  busy,
}: {
  view: OnboardingView;
  act: (action: OnboardingAction) => Promise<OnboardingView>;
  canReview: boolean;
  busy: boolean;
}) => {
  const [config, setConfig] = useState(view.config);
  const baseline = useRef(view.config);
  const [dirty, setDirty] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  useEffect(() => {
    if (!dirty) {
      baseline.current = view.config;
      setConfig(view.config);
    }
  }, [view.config, dirty]);
  const change = <K extends keyof OnboardingConfig>(key: K, value: OnboardingConfig[K]) => {
    setConfig(current => ({ ...current, [key]: value }));
    setDirty(true);
    setMessage('');
  };
  const save = async (event: FormEvent) => {
    event.preventDefault();
    setMessage('');
    setError('');
    const changedFields = Object.fromEntries(
      Object.entries(config).filter(
        ([key, value]) =>
          value !== baseline.current[key as keyof OnboardingConfig] &&
          (canReview || key !== 'readiness')
      )
    ) as Partial<OnboardingConfig>;
    try {
      const fresh = await act({ type: 'configure', config: changedFields });
      setConfig(fresh.config);
      setDirty(false);
      setMessage('Onboarding information saved.');
    } catch (requestError) {
      setError(errorMessage(requestError));
    }
  };
  const dateFields: {
    key:
      | 'goLiveDate'
      | 'migrationDate'
      | 'testFileDueDate'
      | 'finalFileDueDate'
      | 'checklistDueDate';
    label: string;
  }[] = [
    { key: 'goLiveDate', label: 'Go Live Date' },
    { key: 'migrationDate', label: 'Migration Date' },
    { key: 'testFileDueDate', label: 'Test File Due Date' },
    { key: 'finalFileDueDate', label: 'Final File Due Date' },
    { key: 'checklistDueDate', label: 'Checklist Due Date' },
  ];
  return (
    <Section
      title="Dates, Coach & Migration"
      eyebrow="The clinic plan"
      icon={<Settings2 className="h-5 w-5" />}
      description="Date changes update Hub availability automatically. Existing progress and history are retained when the clinic’s dates change."
    >
      <form onSubmit={save} className="space-y-5">
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {dateFields.map(field => (
            <Field label={field.label} key={field.key}>
              <input
                name={field.key}
                type="date"
                className={inputClass}
                value={config[field.key] || ''}
                onChange={event => change(field.key, event.target.value || null)}
                disabled={
                  !config.migrationApplicable &&
                  ['migrationDate', 'testFileDueDate', 'finalFileDueDate'].includes(field.key)
                }
              />
            </Field>
          ))}
          <Field label="Primary Onboarding Coach">
            <select
              name="coachId"
              value={config.coachId || ''}
              onChange={event => change('coachId', event.target.value || null)}
              className={inputClass}
            >
              <option value="">Unassigned · general onboarding support</option>
              {view.content.coaches
                .filter(coach => coach.active || coach.id === config.coachId)
                .map(coach => (
                  <option key={coach.id} value={coach.id} disabled={!coach.active}>
                    {coach.name}
                    {!coach.active && ' · Inactive'}
                  </option>
                ))}
            </select>
            <p className="text-sm text-muted-foreground">
              Choose an active ChiroUp Admin user. Names and email addresses are managed in Admin
              users.
            </p>
          </Field>
        </div>
        <p className="text-base text-muted-foreground sm:text-sm">
          Leave an unknown date blank to display “To Be Determined.” Migration dates display “Not
          Applicable” when migration is not required.
        </p>
        <div className="grid gap-3 md:grid-cols-2">
          <label className="flex cursor-pointer items-center gap-3 rounded-xl border border-border bg-muted/25 p-4 text-base sm:text-sm">
            <input
              type="checkbox"
              name="migrationApplicable"
              className="h-5 w-5 shrink-0 rounded accent-primary-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-700 dark:accent-primary-400"
              checked={config.migrationApplicable}
              onChange={event => change('migrationApplicable', event.target.checked)}
            />
            Migration applies to this clinic
          </label>
          <label className="flex cursor-pointer items-center gap-3 rounded-xl border border-border bg-muted/25 p-4 text-base has-disabled:cursor-not-allowed has-disabled:opacity-60 sm:text-sm">
            <input
              type="checkbox"
              name="consentRequired"
              className="h-5 w-5 shrink-0 rounded accent-primary-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-700 dark:accent-primary-400"
              checked={config.consentRequired}
              disabled={!config.migrationApplicable}
              onChange={event => change('consentRequired', event.target.checked)}
            />
            Signed migration consent is required
          </label>
        </div>
        {canReview && (
          <div className="max-w-lg space-y-3 rounded-xl border border-violet-500/15 bg-violet-50/45 p-4 dark:bg-violet-950/20">
            <Field label="Overall Migration Readiness">
              <select
                name="readiness"
                className={inputClass}
                value={config.readiness}
                disabled={busy || !config.migrationApplicable}
                onChange={event =>
                  change('readiness', event.target.value as OnboardingConfig['readiness'])
                }
              >
                <option value="not_started">Not Started</option>
                <option value="in_progress">In Progress</option>
                <option value="ready">Ready</option>
                <option value="not_applicable" disabled={config.migrationApplicable}>
                  Not Applicable
                </option>
              </select>
            </Field>
            <p className="mt-2 text-base text-muted-foreground sm:text-sm">
              Ready requires internal confirmation. Uploading or replacing a file returns the clinic
              to In Progress.
            </p>
          </div>
        )}
        <div className="flex flex-wrap items-center gap-3 border-t border-border pt-5">
          <button type="submit" className={buttonClass} disabled={busy || !dirty}>
            {busy ? 'Saving…' : 'Save onboarding information'}
          </button>
          {dirty && (
            <p className="text-base font-medium text-amber-800 dark:text-amber-300 sm:text-sm">
              Unsaved changes
            </p>
          )}
          {dirty && (
            <button
              type="button"
              className={secondaryClass}
              disabled={busy}
              onClick={() => {
                setConfig(view.config);
                setDirty(false);
              }}
            >
              Discard changes
            </button>
          )}
          {message && (
            <p role="status" className="text-emerald-700 dark:text-emerald-300">
              {message}
            </p>
          )}
          {error && (
            <p role="alert" className="text-red-600 dark:text-red-400">
              {error}
            </p>
          )}
        </div>
      </form>
    </Section>
  );
};

const ClinicOnboarding = ({ clinicId }: { clinicId: number }) => {
  const onboarding = useOnboarding(clinicId);
  const admin = useAdminMe();
  const role = admin.data?.roleKey;
  const canReview = role === 'super_admin' || role === 'admin' || role === 'tech_admin';
  const canManageContent = role === 'super_admin' || role === 'admin' || role === 'marketing';
  const view = onboarding.data;
  if (onboarding.isLoading) {
    return (
      <div role="status" className="space-y-5 p-6">
        <div className="h-8 w-48 rounded-xl bg-muted motion-safe:animate-pulse" />
        <div className="h-56 rounded-2xl bg-muted motion-safe:animate-pulse" />
        <p className="text-muted-foreground">Loading onboarding…</p>
      </div>
    );
  }
  if (onboarding.isError || !view) {
    return (
      <div
        role="alert"
        className="m-6 space-y-4 rounded-2xl border border-rose-500/20 bg-rose-50 p-6 text-rose-950 dark:bg-rose-950/25 dark:text-rose-100"
      >
        <AlertCircle className="h-7 w-7" aria-hidden="true" />
        <h2 className="text-lg font-semibold">Onboarding information couldn’t load</h2>
        <p>Unable to load this clinic’s onboarding information. {errorMessage(onboarding.error)}</p>
        <button type="button" className={secondaryClass} onClick={() => onboarding.refetch()}>
          Try again
        </button>
      </div>
    );
  }
  return (
    <OnboardingDraftProvider
      key={`${clinicId}:${admin.data?.adminUserId ?? admin.data?.email ?? 'loading'}`}
    >
      <div className="isolate space-y-6 p-4 antialiased sm:space-y-7 sm:p-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="space-y-1">
            <h1 className="text-balance text-2xl font-semibold tracking-tight sm:text-3xl">
              Onboarding
            </h1>
            <p className="text-base text-muted-foreground sm:text-sm">
              Everything the clinic needs for a confident start.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {canManageContent && (
              <Link to="/onboarding/content" className={secondaryClass}>
                Manage shared content <ExternalLink className="h-4 w-4" aria-hidden="true" />
              </Link>
            )}
            <button
              type="button"
              className={secondaryClass}
              disabled={onboarding.isFetching || onboarding.isSaving}
              onClick={() => onboarding.refetch()}
            >
              <RefreshCw className="h-4 w-4" aria-hidden="true" />
              Refresh
            </button>
          </div>
        </div>
        <section className="relative overflow-hidden rounded-2xl bg-slate-950 p-5 text-white sm:p-7">
          <div
            aria-hidden="true"
            className="pointer-events-none absolute -right-12 -top-32 h-80 w-80 rounded-full ring-1 ring-inset ring-white/10"
          />
          <div
            aria-hidden="true"
            className="pointer-events-none absolute -right-2 -top-20 h-56 w-56 rounded-full ring-1 ring-inset ring-white/10"
          />
          <div className="relative flex flex-wrap items-start justify-between gap-4">
            <div className="space-y-2">
              <p className="flex items-center gap-2 font-medium text-primary-300">
                <Rocket className="h-4 w-4" aria-hidden="true" />
                Your Go-Live Journey
              </p>
              <h2 className="max-w-[32ch] text-balance text-2xl font-semibold tracking-tight sm:text-3xl">
                {view.availability.active
                  ? 'A confident start, together.'
                  : 'Onboarding history is preserved'}
              </h2>
              <p className="max-w-[72ch] text-pretty text-base text-slate-300 sm:text-sm">
                {view.availability.active
                  ? 'Keep dates, setup, training, and migration aligned in one place.'
                  : 'Clinic tracking has expired. Checklist, training, and migration submissions are read-only. Authorized reviewers can still update migration readiness. Updating the Go Live Date can reactivate the Hub.'}
              </p>
            </div>
            <div className="inline-flex items-center gap-2 rounded-full bg-white/5 px-3 py-1.5 text-base font-medium text-white ring-1 ring-inset ring-white/15 sm:text-sm">
              <span
                aria-hidden="true"
                className={`h-2 w-2 rounded-full ${view.availability.active ? 'bg-primary-400' : 'bg-slate-400'}`}
              />
              {view.availability.active ? 'Hub active' : 'Read-only history'}
            </div>
          </div>
          <div className="relative mt-6 grid gap-x-8 gap-y-5 border-t border-white/10 pt-6 sm:grid-cols-2 xl:grid-cols-4">
            <div className="space-y-1">
              <p className="text-base text-slate-300 sm:text-sm">Go Live Date</p>
              <p className="text-2xl font-semibold tabular-nums">
                {calendarLabel(view.config.goLiveDate)}
              </p>
              {view.availability.daysUntilGoLive != null && (
                <p className="text-base text-slate-300 sm:text-sm">
                  {view.availability.daysUntilGoLive > 0
                    ? `${view.availability.daysUntilGoLive} days to go`
                    : view.availability.daysUntilGoLive === 0
                      ? 'Go Live is today'
                      : `${Math.abs(view.availability.daysUntilGoLive)} days since Go Live`}
                </p>
              )}
            </div>
            <div className="space-y-1">
              <p className="text-base text-slate-300 sm:text-sm">Setup completion</p>
              <p className="text-2xl font-semibold tabular-nums text-primary-300">
                {view.checklist.percent}%
              </p>
              <progress
                aria-label="Clinic setup completion"
                max={100}
                value={view.checklist.percent}
                className="h-1.5 w-full max-w-48 overflow-hidden rounded-full bg-white/10 accent-primary-400 [&::-webkit-progress-bar]:rounded-full [&::-webkit-progress-bar]:bg-white/10 [&::-webkit-progress-value]:rounded-full [&::-webkit-progress-value]:bg-primary-400 [&::-moz-progress-bar]:rounded-full [&::-moz-progress-bar]:bg-primary-400"
              />
              <p className="text-base text-slate-300 sm:text-sm">
                {view.checklist.completed} of {view.checklist.applicable} applicable categories
              </p>
            </div>
            <div className="space-y-1">
              <p className="text-base text-slate-300 sm:text-sm">Onboarding Coach</p>
              <p className="text-lg font-medium">{view.coach?.name || 'Unassigned'}</p>
              <a
                href={`mailto:${view.coach?.email || view.content.supportEmail}`}
                className="break-all text-base text-primary-300 underline decoration-primary-300/35 underline-offset-4 hover:decoration-primary-300 focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-primary-300 sm:text-sm"
              >
                {view.coach?.email || view.content.supportEmail}
              </a>
            </div>
            <div className="space-y-1">
              <p className="text-base text-slate-300 sm:text-sm">Hub availability</p>
              <p className="text-lg font-medium">
                {view.availability.active ? 'Active' : 'Expired'}
              </p>
              <p className="text-base text-slate-300 sm:text-sm">
                {view.config.goLiveDate
                  ? 'Through 14 days after Go Live'
                  : 'No expiration until Go Live is set'}
              </p>
            </div>
          </div>
        </section>
        <OnboardingConfiguration
          key={clinicId}
          view={view}
          act={onboarding.act}
          canReview={canReview}
          busy={onboarding.isSaving}
        />
        {!!view.locationWarnings.length && (
          <section className="space-y-3 rounded-2xl border border-amber-500/20 bg-amber-50 p-5 text-amber-950 dark:bg-amber-950/25 dark:text-amber-200">
            <h2 className="flex items-center gap-2 font-semibold">
              <CalendarDays className="h-5 w-5" aria-hidden="true" />
              Location setup needs attention
            </h2>
            <ul role="list" className="space-y-1">
              {view.locationWarnings.map(warning => (
                <li key={`${warning.locationId}-${warning.message}`}>
                  <strong>{warning.locationName}:</strong> {warning.message}
                </li>
              ))}
            </ul>
          </section>
        )}
        <OnboardingChecklist
          key={`checklist-${clinicId}`}
          view={view}
          act={onboarding.act}
          busy={onboarding.isSaving}
        />
        <OnboardingTrainingMatrix key={`training-${clinicId}`} view={view} />
        <OnboardingFiles
          key={`files-${clinicId}`}
          clinicId={clinicId}
          view={view}
          canReview={canReview}
          busy={onboarding.isSaving}
          act={onboarding.act}
          upload={onboarding.upload}
        />
      </div>
    </OnboardingDraftProvider>
  );
};

export default ClinicOnboarding;
